import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import PermissionRoute from './PermissionRoute';
import MainLayout from '../layouts/MainLayout';
import Loader from '../components/common/Loader';

const Login          = lazy(() => import('../pages/Auth/Login'));
const Dashboard      = lazy(() => import('../pages/Dashboard/Dashboard'));

const VendorList     = lazy(() => import('../pages/Vendors/VendorList'));
const VendorCreate   = lazy(() => import('../pages/Vendors/VendorCreate'));
const VendorEdit     = lazy(() => import('../pages/Vendors/VendorEdit'));
const VendorView     = lazy(() => import('../pages/Vendors/VendorView'));

const ProposalList   = lazy(() => import('../pages/Proposals/ProposalList'));
const ProposalCreate = lazy(() => import('../pages/Proposals/ProposalCreate'));
const ProposalEdit   = lazy(() => import('../pages/Proposals/ProposalEdit'));
const ProposalView   = lazy(() => import('../pages/Proposals/ProposalView'));
const ProposalPrint  = lazy(() => import('../pages/Proposals/ProposalPrint'));

const BillList       = lazy(() => import('../pages/Bills/BillList'));
const BillCreate     = lazy(() => import('../pages/Bills/BillCreate'));
const BillEdit       = lazy(() => import('../pages/Bills/BillEdit'));
const BillView       = lazy(() => import('../pages/Bills/BillView'));
const BillInvoice    = lazy(() => import('../pages/Bills/BillInvoice'));

const PaymentList    = lazy(() => import('../pages/Payments/PaymentList'));
const PaymentCreate  = lazy(() => import('../pages/Payments/PaymentCreate'));
const PaymentEdit    = lazy(() => import('../pages/Payments/PaymentEdit'));
const PaymentView    = lazy(() => import('../pages/Payments/PaymentView'));

const ProjectList    = lazy(() => import('../pages/Projects/ProjectList'));
const ProjectForm    = lazy(() => import('../pages/Projects/ProjectForm'));

const HostingList    = lazy(() => import('../pages/Hostings/HostingList'));
const HostingForm    = lazy(() => import('../pages/Hostings/HostingForm'));

const NoteList       = lazy(() => import('../pages/ProjectNotes/NoteList'));
const NoteCreate     = lazy(() => import('../pages/ProjectNotes/NoteCreate'));
const NoteEdit       = lazy(() => import('../pages/ProjectNotes/NoteEdit'));
const NoteView       = lazy(() => import('../pages/ProjectNotes/NoteView'));

const Profile        = lazy(() => import('../pages/Auth/Profile'));
const ChangePassword = lazy(() => import('../pages/Auth/ChangePassword'));

const Documentation  = lazy(() => import('../pages/Documentation/Documentation'));

const RoleList       = lazy(() => import('../pages/Admin/RoleList'));
const RoleForm       = lazy(() => import('../pages/Admin/RoleForm'));
const UserList       = lazy(() => import('../pages/Admin/UserList'));
const UserForm       = lazy(() => import('../pages/Admin/UserForm'));
const ModuleList     = lazy(() => import('../pages/Admin/ModuleList'));
const ModuleForm     = lazy(() => import('../pages/Admin/ModuleForm'));
const NoAccess       = lazy(() => import('../pages/NoAccess'));

const Wrap = ({ children }) => <Suspense fallback={<Loader />}>{children}</Suspense>;

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Wrap><Login /></Wrap>} />

      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Wrap><Dashboard /></Wrap>} />

          <Route path="/vendors"           element={<Wrap><VendorList /></Wrap>} />
          <Route path="/vendors/create"    element={<Wrap><VendorCreate /></Wrap>} />
          <Route path="/vendors/edit/:id"  element={<Wrap><VendorEdit /></Wrap>} />
          <Route path="/vendors/view/:id"  element={<Wrap><VendorView /></Wrap>} />

          <Route path="/proposals"          element={<PermissionRoute permission="proposals.view"><Wrap><ProposalList /></Wrap></PermissionRoute>} />
          <Route path="/proposals/create"   element={<PermissionRoute permission="proposals.create"><Wrap><ProposalCreate /></Wrap></PermissionRoute>} />
          <Route path="/proposals/edit/:id" element={<PermissionRoute permission="proposals.edit"><Wrap><ProposalEdit /></Wrap></PermissionRoute>} />
          <Route path="/proposals/view/:id" element={<PermissionRoute permission="proposals.view"><Wrap><ProposalView /></Wrap></PermissionRoute>} />
          <Route path="/proposals/print/:id" element={<PermissionRoute permission="proposals.view"><Wrap><ProposalPrint /></Wrap></PermissionRoute>} />

          <Route path="/bills"             element={<Wrap><BillList /></Wrap>} />
          <Route path="/bills/create"      element={<Wrap><BillCreate /></Wrap>} />
          <Route path="/bills/edit/:id"    element={<Wrap><BillEdit /></Wrap>} />
          <Route path="/bills/view/:id"    element={<Wrap><BillView /></Wrap>} />
          <Route path="/bills/invoice/:id" element={<Wrap><BillInvoice /></Wrap>} />

          <Route path="/payments"          element={<Wrap><PaymentList /></Wrap>} />
          <Route path="/payments/create"   element={<Wrap><PaymentCreate /></Wrap>} />
          <Route path="/payments/edit/:id" element={<Wrap><PaymentEdit /></Wrap>} />
          <Route path="/payments/view/:id" element={<Wrap><PaymentView /></Wrap>} />

          <Route path="/projects"          element={<PermissionRoute permission="projects.view"><Wrap><ProjectList /></Wrap></PermissionRoute>} />
          <Route path="/projects/create"   element={<PermissionRoute permission="projects.create"><Wrap><ProjectForm mode="create" /></Wrap></PermissionRoute>} />
          <Route path="/projects/edit/:id" element={<PermissionRoute permission="projects.edit"><Wrap><ProjectForm mode="edit" /></Wrap></PermissionRoute>} />

          <Route path="/hostings"          element={<PermissionRoute permission="hostings.view"><Wrap><HostingList /></Wrap></PermissionRoute>} />
          <Route path="/hostings/create"   element={<PermissionRoute permission="hostings.create"><Wrap><HostingForm mode="create" /></Wrap></PermissionRoute>} />
          <Route path="/hostings/edit/:id" element={<PermissionRoute permission="hostings.edit"><Wrap><HostingForm mode="edit" /></Wrap></PermissionRoute>} />

          <Route path="/project-notes"          element={<Wrap><NoteList /></Wrap>} />
          <Route path="/project-notes/create"   element={<Wrap><NoteCreate /></Wrap>} />
          <Route path="/project-notes/edit/:id" element={<Wrap><NoteEdit /></Wrap>} />
          <Route path="/project-notes/view/:id" element={<Wrap><NoteView /></Wrap>} />

          <Route path="/profile"          element={<Wrap><Profile /></Wrap>} />
          <Route path="/change-password"  element={<Wrap><ChangePassword /></Wrap>} />

          <Route path="/documentation"    element={<Wrap><Documentation /></Wrap>} />

          {/* ── Administration (permission-gated) ── */}
          <Route path="/roles"            element={<PermissionRoute permission="roles.view"><Wrap><RoleList /></Wrap></PermissionRoute>} />
          <Route path="/roles/create"     element={<PermissionRoute permission="roles.create"><Wrap><RoleForm mode="create" /></Wrap></PermissionRoute>} />
          <Route path="/roles/edit/:id"   element={<PermissionRoute permission="roles.edit"><Wrap><RoleForm mode="edit" /></Wrap></PermissionRoute>} />
          <Route path="/roles/view/:id"   element={<PermissionRoute permission="roles.view"><Wrap><RoleForm mode="view" /></Wrap></PermissionRoute>} />

          <Route path="/users"            element={<PermissionRoute permission="users.view"><Wrap><UserList /></Wrap></PermissionRoute>} />
          <Route path="/users/create"     element={<PermissionRoute permission="users.create"><Wrap><UserForm mode="create" /></Wrap></PermissionRoute>} />
          <Route path="/users/edit/:id"   element={<PermissionRoute permission="users.edit"><Wrap><UserForm mode="edit" /></Wrap></PermissionRoute>} />

          <Route path="/modules"          element={<PermissionRoute permission="modules.view"><Wrap><ModuleList /></Wrap></PermissionRoute>} />
          <Route path="/modules/create"   element={<PermissionRoute permission="modules.create"><Wrap><ModuleForm mode="create" /></Wrap></PermissionRoute>} />
          <Route path="/modules/edit/:id" element={<PermissionRoute permission="modules.edit"><Wrap><ModuleForm mode="edit" /></Wrap></PermissionRoute>} />

          <Route path="/no-access"        element={<Wrap><NoAccess /></Wrap>} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
